import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';

const Disclaimer = () => {
  const { announce } = useAccessibility();

  useEffect(() => {
    // Announce page load to screen readers
    announce("Legal disclaimer and privacy policy page loaded", "polite");
  }, [announce]);

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Paper 
          elevation={2} 
          sx={{ p: 4 }}
          role="main"
          aria-labelledby="disclaimer-title"
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            id="disclaimer-title"
          >
            Legal Disclaimer & Privacy Policy
          </Typography>
          
          {/* Legal Disclaimer Section */}
          <Box component="section" aria-labelledby="legal-disclaimer-heading">
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              sx={{ mt: 4, mb: 2, color: 'primary.main' }}
              id="legal-disclaimer-heading"
            >
              Legal Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              The gear sold on this website is designed to help reduce the likelihood of bee or wasp stings, but it is not certified protective equipment and does not guarantee protection.
            </Typography>
            <Typography variant="body1" paragraph>
              These products are not a substitute for professional protective suits and should not be relied upon in high-risk environments such as professional beekeeping or known wasp infestations.
            </Typography>
            <Typography variant="body1" paragraph>
              No medical claims are made regarding the prevention or treatment of stings or allergic reactions. For medical concerns or known allergies, consult a licensed healthcare professional.
            </Typography>
            <Typography variant="body1" paragraph>
              By using our products, you acknowledge and accept the inherent risks involved in interacting with bees, wasps, or other stinging insects.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Privacy Policy Section */}
          <Box component="section" aria-labelledby="privacy-policy-heading">
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              sx={{ color: 'primary.main' }}
              id="privacy-policy-heading"
            >
              Privacy Policy
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information you provide directly to us, such as when you create an account, make a purchase, subscribe to our newsletter, or contact us. This may include your name, email address, phone number, shipping address, and payment information.
            </Typography>
            <Typography variant="body1" paragraph>
              We also automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, and pages viewed.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1">Process and fulfill your orders</Typography>
              <Typography component="li" variant="body1">Communicate with you about your orders and account</Typography>
              <Typography component="li" variant="body1">Send you marketing communications (with your consent)</Typography>
              <Typography component="li" variant="body1">Improve our website and customer experience</Typography>
              <Typography component="li" variant="body1">Comply with legal obligations</Typography>
            </Box>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Information Sharing
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information with:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1">Service providers who assist with our business operations</Typography>
              <Typography component="li" variant="body1">Payment processors to handle transactions</Typography>
              <Typography component="li" variant="body1">Shipping companies to deliver your orders</Typography>
              <Typography component="li" variant="body1">Legal authorities when required by law</Typography>
            </Box>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and understand where our visitors are coming from. You can control cookies through your browser settings.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Social Media Integration
            </Typography>
            <Typography variant="body1" paragraph>
              Our website may include social media features, such as links to our Instagram, Facebook, and Bluesky accounts. These features may collect your IP address and set cookies. Your interactions with these features are governed by the privacy policies of the respective social media platforms.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to access, update, or delete your personal information. You may also opt out of marketing communications at any time. To exercise these rights, please contact us using the information provided below.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this privacy policy or our privacy practices, please contact us through our contact page or by email.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Disclaimer; 